import { useState, useEffect } from 'react';
import api from '../../services/api';
import Modal from '../UI/Modal';
import DeliveryEditForm from '../Planning/DeliveryEditForm';

export default function PatientFiche({ patientId, onBack }) {
    const [patient, setPatient] = useState(null);
    const [secteurs, setSecteurs] = useState([]);
    const [users, setUsers] = useState([]);
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [livraisons, setLivraisons] = useState([]);
    const [loadingLivraisons, setLoadingLivraisons] = useState(false);
    const [selectedDelivery, setSelectedDelivery] = useState(null);

    useEffect(() => {
        loadPatient();
        loadSecteurs();
        loadUsers();
        loadLivraisons();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [patientId]);

    const loadPatient = async () => {
        try {
            const res = await api.get(`/patients/${patientId}`);
            setPatient(res.data);
            setForm({
                nom: res.data.nom || '', ville: res.data.ville || '', adresse: res.data.adresse || '',
                adresseSecondaire: res.data.adresseSecondaire || '', villeSecondaire: res.data.villeSecondaire || '',
                telephone: res.data.telephone || '', actif: res.data.actif ?? true,
                pathologie: res.data.pathologie || '', statutPatient: res.data.statutPatient || 'actif',
                secteur: res.data.secteur?.['@id'] || '', chu: res.data.chu || '', livreur: res.data.livreur?.['@id'] || '',
                molecule: res.data.molecule || '',
                isRetro: res.data.isRetro || false,
            });
        } catch (err) {
            console.error(err);
        }
    };

    const loadSecteurs = async () => {
        try {
            const res = await api.get('/secteurs');
            setSecteurs(res.data.member || []);
        } catch (err) { console.error(err); }
    };

    const loadUsers = async () => {
        try {
            const res = await api.get('/users');
            setUsers(res.data.member || []);
        } catch (err) { console.error(err); }
    };

    const loadLivraisons = async () => {
        setLoadingLivraisons(true);
        try {
            const res = await api.get(`/deliveries?patient=/api/patients/${patientId}&order[date]=desc&itemsPerPage=300`);
            setLivraisons(res.data.member || []);
        } catch (err) {
            console.error(err);
        } finally {
            setLoadingLivraisons(false);
        }
    };

    const handleSave = async () => {
        if (!form) return;
        setSaving(true);
        try {
            const payload = { ...form, livreur: form.livreur || null, secteur: form.secteur || null, chu: form.chu || null };
            await api.patch(`/patients/${patientId}`, payload, { headers: { 'Content-Type': 'application/merge-patch+json' } });
            setSaved(true);
            setTimeout(() => setSaved(false), 2000);
            loadPatient();
        } catch (err) {
            console.error(err);
        } finally {
            setSaving(false);
        }
    };

    if (!patient || !form) {
        return (
            <div style={{ padding: '24px', textAlign: 'center', color: '#888' }}>
                Chargement de la fiche patient...
            </div>
        );
    }

    return (
        <div style={{ padding: '24px', maxWidth: '1100px' }}>
            {/* Header avec bouton retour */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
                <button onClick={onBack} style={backButtonStyle}>← Retour aux patients</button>
                <h2 style={{ margin: 0, fontSize: '22px', fontWeight: '700', color: '#1a1a2e' }}>
                    {patient.nom}
                </h2>
                {saved && (
                    <span style={{ backgroundColor: '#dcfce7', color: '#16a34a', padding: '6px 14px', borderRadius: '8px', fontSize: '13px', fontWeight: '600' }}>
                        ✅ Enregistré
                    </span>
                )}
                <button onClick={handleSave} disabled={saving} style={{ ...submitButtonStyle, marginLeft: 'auto' }}>
                    {saving ? 'Enregistrement...' : '💾 Enregistrer les modifications'}
                </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', alignItems: 'start' }}>

                {/* Colonne gauche — Informations patient */}
                <div style={sectionCardStyle}>
                    <h3 style={sectionTitleStyle}>📋 Informations</h3>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                        <div style={fieldStyle}>
                            <label style={labelStyle}>Nom</label>
                            <input type="text" value={form.nom} onChange={e => setForm({ ...form, nom: e.target.value })} style={inputStyle} />
                        </div>
                        <div style={fieldStyle}>
                            <label style={labelStyle}>Téléphone</label>
                            <input type="tel" value={form.telephone} onChange={e => setForm({ ...form, telephone: e.target.value })} style={inputStyle} />
                        </div>
                    </div>

                    <div style={{ marginTop: '4px', marginBottom: '4px', fontSize: '12px', fontWeight: '700', color: '#888', textTransform: 'uppercase' }}>
                        🏠 Adresse principale
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '12px' }}>
                        <div style={fieldStyle}>
                            <label style={labelStyle}>Adresse</label>
                            <input type="text" value={form.adresse} onChange={e => setForm({ ...form, adresse: e.target.value })} style={inputStyle} />
                        </div>
                        <div style={fieldStyle}>
                            <label style={labelStyle}>Ville</label>
                            <input type="text" value={form.ville} onChange={e => setForm({ ...form, ville: e.target.value })} style={inputStyle} />
                        </div>
                    </div>

                    <div style={{ marginTop: '4px', marginBottom: '4px', fontSize: '12px', fontWeight: '700', color: '#888', textTransform: 'uppercase' }}>
                        🏡 Adresse secondaire
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '12px' }}>
                        <div style={fieldStyle}>
                            <label style={labelStyle}>Adresse</label>
                            <input type="text" value={form.adresseSecondaire} onChange={e => setForm({ ...form, adresseSecondaire: e.target.value })} style={inputStyle} placeholder="Résidence secondaire, vacances..." />
                        </div>
                        <div style={fieldStyle}>
                            <label style={labelStyle}>Ville</label>
                            <input type="text" value={form.villeSecondaire} onChange={e => setForm({ ...form, villeSecondaire: e.target.value })} style={inputStyle} />
                        </div>
                    </div>

                    <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '4px'}}>
                        <div style={fieldStyle}>
                            <label style={labelStyle}>Pathologie</label>
                            <select value={form.pathologie || ''}
                                    onChange={e => setForm({...form, pathologie: e.target.value})} style={inputStyle}>
                                <option value="">— Non définie —</option>
                                <option value="nutrition">🟢 Nutrition</option>
                                <option value="perfusion">🔵 Perfusion</option>
                                <option value="parkinson">⚫ Parkinson</option>
                            </select>
                        </div>
                        <div style={fieldStyle}>
                            <label style={labelStyle}>Statut patient</label>
                            <select value={form.statutPatient || 'actif'}
                                    onChange={e => setForm({...form, statutPatient: e.target.value})}
                                    style={inputStyle}>
                                <option value="actif">✅ Actif</option>
                                <option value="en_pause">⏸️ En pause</option>
                                <option value="hospitalise">🏥 Hospitalisé</option>
                                <option value="ftt">🔴 FTT</option>
                            </select>
                        </div>
                        <div style={fieldStyle}>
                            <label style={labelStyle}>Secteur CHU</label>
                            <select value={form.secteur || ''}
                                    onChange={e => setForm({...form, secteur: e.target.value})} style={inputStyle}>
                                <option value="">— Non défini —</option>
                                {secteurs.map(s => <option key={s['@id']} value={s['@id']}>{s.nom}</option>)}
                            </select>
                        </div>
                        <div style={fieldStyle}>
                            <label style={labelStyle}>CHU Rétrocession</label>
                            <select value={form.chu || ''} onChange={e => setForm({...form, chu: e.target.value})}
                                    style={inputStyle}>
                                <option value="">— Aucun —</option>
                                <option value="nancy">🟡 Nancy</option>
                                <option value="epinal">🔵 Épinal</option>
                                <option value="bar_le_duc">🟢 Bar-le-Duc</option>
                                <option value="neufchateau">🔴 Neufchâteau</option>
                            </select>
                        </div>
                        <div style={fieldStyle}>
                            <label style={labelStyle}>Molécule</label>
                            <select value={form.molecule || ''}
                                    onChange={e => setForm({...form, molecule: e.target.value})} style={inputStyle}>
                                <option value="">— Aucune —</option>
                                <option value="C">C — Cuvitru</option>
                                <option value="X">X — Xembify</option>
                                <option value="H">H — Hizentra</option>
                                <option value="CUT">CUT — Cutaquig</option>
                                <option value="CLAI">CLAI — Clairyg</option>
                                <option value="P">P — Prolastin</option>
                                <option value="A">A — Alfalastin</option>
                                <option value="R">R — Reespreza</option>
                            </select>
                        </div>
                        <div style={fieldStyle}>
                            <label style={labelStyle}>Livreur attitré</label>
                            <select value={form.livreur || ''}
                                    onChange={e => setForm({...form, livreur: e.target.value})} style={inputStyle}>
                                <option value="">— Aucun —</option>
                                {users.map(u => <option key={u['@id']} value={u['@id']}>{u.nom || u.email}</option>)}
                            </select>
                        </div>
                        <div style={{...fieldStyle, display: 'flex', alignItems: 'center', gap: '8px'}}>
                            <input
                                type="checkbox"
                                id="isRetro"
                                checked={form.isRetro || false}
                                onChange={e => setForm({...form, isRetro: e.target.checked})}
                                style={{width: '16px', height: '16px', cursor: 'pointer'}}
                            />
                            <label htmlFor="isRetro" style={{...labelStyle, margin: 0, cursor: 'pointer'}}>
                                🔴 Patient rétrocession (affiché en priorité et en rouge)
                            </label>
                        </div>
                    </div>
                </div>

                {/* Colonne droite — Historique livraisons */}
                <div style={sectionCardStyle}>
                    <h3 style={sectionTitleStyle}>🚚 Livraisons ({livraisons.length})</h3>

                    {loadingLivraisons ? (
                        <div style={{padding: '24px', textAlign: 'center', color: '#888'}}>Chargement...</div>
                    ) : livraisons.length === 0 ? (
                        <div style={{padding: '24px', textAlign: 'center', color: '#888'}}>Aucune livraison</div>
                    ) : (
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '6px',
                            maxHeight: '440px', // ≈ 5-6 lignes visibles, scroll au-delà
                            overflowY: 'auto',
                            paddingRight: '4px',
                        }}>
                            {livraisons.map(l => (
                                <div
                                    key={l.id}
                                    onClick={() => setSelectedDelivery(l)}
                                    style={{
                                        backgroundColor: '#f8fafc',
                                        borderRadius: '8px',
                                        padding: '10px 14px',
                                        fontSize: '13px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '10px',
                                        flexWrap: 'wrap',
                                        cursor: 'pointer',
                                        borderLeft: `3px solid ${
                                            l.statutPrep === 'confirme' ? '#3b82f6' :
                                                l.statutPrep === 'prepare' ? '#22c55e' :
                                                    l.statutPrep === 'annule' ? '#ef4444' :
                                                        l.statutPrep === 'en_attente' ? '#ea580c' : '#f59e0b'
                                        }`,
                                    }}
                                    onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f0f2ff'}
                                    onMouseLeave={e => e.currentTarget.style.backgroundColor = '#f8fafc'}
                                >
                                    <span style={{
                                        fontWeight: '700',
                                        color: '#1a1a2e',
                                        whiteSpace: 'nowrap'
                                    }}>{new Date(l.date).toLocaleDateString('fr-FR')}</span>
                                    <span style={{
                                        color: '#888',
                                        whiteSpace: 'nowrap'
                                    }}>{l.periode === 'matin' ? '🌅 Matin' : '🌇 Après-midi'}</span>
                                    {l.heureDebut && <span style={{
                                        color: '#888',
                                        whiteSpace: 'nowrap'
                                    }}>{l.heureDebut.substring(11, 16)}</span>}
                                    <span style={{color: '#444'}}>{l.livreur?.nom || '—'}</span>
                                    {l.notes && <span style={{
                                        color: '#888',
                                        fontStyle: 'italic',
                                        flex: 1,
                                        minWidth: '80px',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                    }}>{l.notes}</span>}
                                    <span style={{
                                        fontSize: '11px',
                                        fontWeight: '600',
                                        whiteSpace: 'nowrap',
                                        padding: '2px 8px',
                                        borderRadius: '20px',
                                        backgroundColor: l.statut === 'livré' ? '#dcfce7' : '#f0f2f5',
                                        color: l.statut === 'livré' ? '#16a34a' : '#888',
                                    }}>{l.statut}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
            {/* Modale édition livraison */}
            {selectedDelivery && (
                <Modal title="Modifier la livraison" onClose={() => setSelectedDelivery(null)}>
                    <DeliveryEditForm
                        delivery={selectedDelivery}
                        onSave={() => {
                            setSelectedDelivery(null);
                            loadLivraisons();
                        }}
                        onCancel={() => setSelectedDelivery(null)}
                        onDelete={() => {
                            setSelectedDelivery(null);
                            loadLivraisons();
                        }}
                    />
                </Modal>
            )}
        </div>
    );
}

const backButtonStyle = {
    padding: '8px 16px',
    backgroundColor: '#f0f2f5',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
    color: '#444'
};
const sectionCardStyle = {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '20px',
    boxShadow: '0 1px 6px rgba(0,0,0,0.07)'
};
const sectionTitleStyle = {margin: '0 0 16px', fontSize: '15px', fontWeight: '700', color: '#1a1a2e'};
const fieldStyle = {marginBottom: '14px'};
const labelStyle = {display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#444'};
const inputStyle = {
    width: '100%',
    padding: '9px 12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '14px', boxSizing: 'border-box' };
const submitButtonStyle = { padding: '10px 20px', backgroundColor: '#4f46e5', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' };